/**
 * `CountryStatusMap` — world choropleth shaded by per-country hantavirus
 * surveillance status.
 *
 * Data source: `regions.countriesProjection` (TanStack Query, refetches
 * every 5 min, stale after 60 s). Each row carries `iso_a2`, `status`,
 * `detail`, `source_url`, and `verified_at` provenance fields.
 *
 * Map layer:
 *   - GeoJSON source: `/data/world-countries.geojson` (Natural Earth 50m)
 *   - Fill layer with `fill-color` data-driven by `ISO_A2` feature property
 *     mapped via a MapLibre `match` expression to 5 status colours.
 *   - Click popup: country name, status badge, detail text, source link,
 *     verified_at relative time.
 *
 * Fallback: if MapLibre fails (WebGL disabled, CDN outage) a static
 * message with a link to the regional table is shown.
 *
 * SSR safety: map instance created inside `useEffect`.
 *
 * RTL: logical CSS properties only; no physical directional classes.
 */

import maplibregl, { type Map as MapLibreMap, type Popup as MapLibrePopup } from "maplibre-gl";
import { useEffect, useRef, useState } from "react";

import { type CountryStatusRow, useCountryStatus } from "#/hooks/useCountryStatus.js";
import type { TranslateFn } from "#/lib/i18n.js";

/* ---------------------------------------------------------------
 * Constants
 * --------------------------------------------------------------- */

const MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/dark";
const COUNTRIES_GEOJSON_URL = "/data/world-countries.geojson";
const DEFAULT_CENTER: [number, number] = [0, 20];
const DEFAULT_ZOOM = 1.5;

/** Status → fill colour (Uncodixify: solid semantic colours, no gradients). */
export const STATUS_COLOR: Record<string, string> = {
  confirmed: "#dc2626",
  suspected: "#f59e0b",
  monitoring: "#3b82f6",
  cleared: "#22c55e",
  unknown: "#9ca3af",
};
const DEFAULT_FILL = "#9ca3af";

/** Legend order (highest to lowest severity). */
const LEGEND_KEYS = ["confirmed", "suspected", "monitoring", "cleared", "unknown"] as const;

/* ---------------------------------------------------------------
 * Pure helpers — exported for unit tests
 * --------------------------------------------------------------- */

/**
 * Build a MapLibre `match` expression that maps each `ISO_A2` code to its
 * status colour. Returns the plain default colour when there are no rows.
 */
export function buildMatchExpr(rows: CountryStatusRow[]): unknown {
  if (rows.length === 0) return DEFAULT_FILL;
  const pairs: string[] = [];
  for (const row of rows) {
    pairs.push(row.iso_a2.toUpperCase(), STATUS_COLOR[row.status] ?? DEFAULT_FILL);
  }
  // ["match", ["get", "ISO_A2"], code1, color1, ..., defaultColor]
  return ["match", ["get", "ISO_A2"], ...pairs, DEFAULT_FILL];
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function relativeTime(iso: string, locale: string): string {
  const d = new Date(iso);
  const diffSecs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSecs < 5) {
    return locale === "ar" ? "الآن" : locale === "fr" ? "à l'instant" : "just now";
  }
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (diffSecs < 60) return rtf.format(-diffSecs, "second");
  if (diffSecs < 3_600) return rtf.format(-Math.floor(diffSecs / 60), "minute");
  if (diffSecs < 86_400) return rtf.format(-Math.floor(diffSecs / 3_600), "hour");
  return rtf.format(-Math.floor(diffSecs / 86_400), "day");
}

function localizedCountryName(code: string, fallback: string, locale: string): string {
  if (!code) return fallback;
  const upper = code.toUpperCase();
  try {
    const dn = new Intl.DisplayNames([locale, "en"], { type: "region" });
    const name = dn.of(upper);
    return name && name !== upper ? name : fallback;
  } catch {
    return fallback;
  }
}

/* ---------------------------------------------------------------
 * Popup HTML builder (string-based — required by MapLibre)
 * --------------------------------------------------------------- */

function buildPopupHtml(
  countryName: string,
  row: CountryStatusRow | undefined,
  locale: string,
  t: TranslateFn,
): string {
  const parts: string[] = [];

  parts.push(
    `<strong style="font-family:var(--font-sans);font-size:0.9rem;display:block;margin-bottom:0.375rem;">${esc(countryName)}</strong>`,
  );

  if (!row) {
    parts.push(
      `<p style="margin:0;color:var(--color-fg-muted);font-size:0.8rem;">${esc(t("tooltip.noData"))}</p>`,
    );
    return `<div style="min-width:160px;">${parts.join("")}</div>`;
  }

  const color = STATUS_COLOR[row.status] ?? DEFAULT_FILL;
  parts.push(`
    <p style="margin:0.25rem 0;font-size:0.8rem;">
      <span style="color:var(--color-fg-muted);margin-inline-end:0.3rem;">${esc(t("tooltip.status"))}:</span>
      <span style="color:${esc(color)};font-weight:600;">${esc(t(`status.${row.status}`))}</span>
    </p>`);

  if (row.detail) {
    parts.push(
      `<p style="margin:0.25rem 0;font-size:0.8rem;color:var(--color-fg-muted);">${esc(row.detail)}</p>`,
    );
  }

  if (row.source_url) {
    parts.push(`
      <p style="margin:0.5rem 0 0;font-size:0.75rem;">
        <span style="color:var(--color-fg-muted);">${esc(t("tooltip.source"))}: </span>
        <a href="${esc(row.source_url)}" target="_blank" rel="noopener noreferrer"
           style="color:var(--color-accent-info);text-decoration:underline;word-break:break-all;">
          ${esc(safeHostname(row.source_url))}
        </a>
      </p>`);
  }

  if (row.verified_at) {
    parts.push(
      `<p style="margin:0.25rem 0 0;font-size:0.75rem;color:var(--color-fg-muted);">
        ${esc(t("tooltip.verifiedAt"))}: ${esc(relativeTime(row.verified_at, locale))}
      </p>`,
    );
  }

  return `<div style="min-width:180px;max-width:280px;">${parts.join("")}</div>`;
}

/* ---------------------------------------------------------------
 * Props
 * --------------------------------------------------------------- */

export interface CountryStatusMapProps {
  locale: string;
  t: TranslateFn;
  apiBaseUrl?: string;
}

/* ---------------------------------------------------------------
 * CountryStatusMap
 * --------------------------------------------------------------- */

export function CountryStatusMap({ locale, t, apiBaseUrl }: CountryStatusMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const popupRef = useRef<MapLibrePopup | null>(null);

  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  // Stable refs — read inside event handlers without re-running effects.
  const tRef = useRef(t);
  tRef.current = t;
  const localeRef = useRef(locale);
  localeRef.current = locale;

  // TanStack Query: live per-country status rows.
  const { data: rows = [], isError } = useCountryStatus({ apiBaseUrl, locale });
  const rowsRef = useRef<CountryStatusRow[]>(rows);
  rowsRef.current = rows;

  // Signature used to skip redundant setPaintProperty calls.
  const paintedSigRef = useRef("");

  /* ----------------------------------------------------------------
   * 1. Map construction (mount only).
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    let map: MapLibreMap;
    try {
      map = new maplibregl.Map({
        container,
        style: MAP_STYLE_URL,
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        attributionControl: { compact: true },
        pitch: 0,
        bearing: 0,
        pitchWithRotate: false,
        dragRotate: false,
        renderWorldCopies: false,
      });
    } catch {
      setHasFailed(true);
      return undefined;
    }

    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    map.on("error", (e) => {
      console.warn(
        "[CountryStatusMap]",
        (e as { error?: { message?: string } })?.error?.message ?? e,
      );
    });

    map.on("load", () => {
      // Insert choropleth layers before the first symbol layer so labels
      // remain visible on top.
      const firstSymbolId = map.getStyle().layers?.find((l) => l.type === "symbol")?.id;

      map.addSource("world-countries", {
        type: "geojson",
        data: COUNTRIES_GEOJSON_URL,
      });

      // Fill layer — initial colour is all-unknown; setPaintProperty
      // updates it once real data arrives.
      map.addLayer(
        {
          id: "countries-fill",
          type: "fill",
          source: "world-countries",
          paint: {
            "fill-color": DEFAULT_FILL,
            "fill-opacity": 0.65,
          },
        },
        firstSymbolId,
      );

      // Outline layer sits above fill but still below labels.
      map.addLayer(
        {
          id: "countries-outline",
          type: "line",
          source: "world-countries",
          paint: {
            "line-color": "hsl(220 14% 22%)",
            "line-width": 0.5,
            "line-opacity": 0.9,
          },
        },
        firstSymbolId,
      );

      // Click → popup.
      map.on("click", "countries-fill", (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const props = feature.properties ?? {};
        const isoA2 = typeof props.ISO_A2 === "string" ? props.ISO_A2.toUpperCase() : "";
        const fallbackName = typeof props.ADMIN === "string" && props.ADMIN ? props.ADMIN : isoA2;
        const countryName = localizedCountryName(isoA2, fallbackName, localeRef.current);
        const row = rowsRef.current.find((r) => r.iso_a2.toUpperCase() === isoA2);

        const html = buildPopupHtml(countryName, row, localeRef.current, tRef.current);
        popupRef.current?.remove();
        popupRef.current = new maplibregl.Popup({
          closeButton: true,
          closeOnClick: true,
          maxWidth: "300px",
        })
          .setLngLat(e.lngLat)
          .setHTML(html)
          .addTo(map);
      });

      map.on("mouseenter", "countries-fill", () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", "countries-fill", () => {
        map.getCanvas().style.cursor = "";
      });

      setIsStyleLoaded(true);
    });

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []); // mount only

  /* ----------------------------------------------------------------
   * 2. Sync status rows → fill-color expression on data changes.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded) return;

    const sig = rows.map((r) => `${r.iso_a2}:${r.status}:${r.verified_at}`).join(",");
    if (sig === paintedSigRef.current) return;
    paintedSigRef.current = sig;

    map.setPaintProperty("countries-fill", "fill-color", buildMatchExpr(rows));
  }, [rows, isStyleLoaded]);

  /* ----------------------------------------------------------------
   * 3. Render.
   * ---------------------------------------------------------------- */

  if (hasFailed || isError) {
    return (
      <section className="rounded-lg border border-border bg-card" aria-label={t("ariaLabel")}>
        <div className="flex flex-col items-center gap-3 p-8 text-center">
          <p className="text-sm text-muted-foreground">{t("error")}</p>
          <a
            href="#regions"
            className="text-sm text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          >
            {t("fallbackLink")}
          </a>
        </div>
      </section>
    );
  }

  return (
    <section
      className="rounded-lg border border-border bg-card overflow-hidden"
      aria-label={t("ariaLabel")}
    >
      {/* Map canvas wrapper */}
      <div className="relative">
        <div
          ref={containerRef}
          className="outbreak-map-canvas w-full"
          style={{ height: "380px" }}
          data-testid="country-status-map-canvas"
        />
        {!isStyleLoaded && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-card text-sm text-muted-foreground"
            aria-hidden="true"
          >
            {t("loading")}
          </div>
        )}
      </div>

      {/* Legend */}
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-t border-border"
        role="list"
        aria-label={t("legend.title")}
      >
        <span className="text-xs font-medium text-muted-foreground me-1 shrink-0">
          {t("legend.title")}
        </span>
        {LEGEND_KEYS.map((key) => (
          <span
            key={key}
            role="listitem"
            className="flex items-center gap-1.5 text-xs text-foreground"
          >
            <span
              className="inline-block h-3 w-3 rounded-sm shrink-0"
              style={{ backgroundColor: STATUS_COLOR[key] }}
              aria-hidden="true"
            />
            {t(`legend.${key}`)}
          </span>
        ))}
      </div>

      {/* Screen-reader description */}
      <p className="sr-only">{t("subtitle")}</p>
    </section>
  );
}

export default CountryStatusMap;
